import { forwardRef, type ComponentPropsWithoutRef } from 'react';

import { cx } from '../internal/cx.js';

export type HeadingLevel = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
export type HeadingSize = '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9';
/** Design-system brand scale (D1, D2, H1–H7). Overrides `size` when set. */
export type HeadingScale = 'd1' | 'd2' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'h7';
export type HeadingWeight = 'regular' | 'medium' | 'bold';

const DEFAULT_SIZE_BY_LEVEL: Record<HeadingLevel, HeadingSize> = {
  h1: '8',
  h2: '7',
  h3: '6',
  h4: '5',
  h5: '4',
  h6: '3',
};

export type HeadingProps = ComponentPropsWithoutRef<'h1'> & {
  /** Semantic heading level. */
  as?: HeadingLevel;
  size?: HeadingSize;
  /** Design-system brand scale. Overrides `size` when set. */
  scale?: HeadingScale;
  weight?: HeadingWeight;
};

/** Heading primitive with semantic level and type scale. Requires `@import '@nrx/ui/theme.css'`. */
export const Heading = forwardRef<HTMLHeadingElement, HeadingProps>(function Heading(
  { as: As = 'h1', className, size, scale, weight = 'bold', ...rest },
  ref,
): React.JSX.Element {
  const resolvedSize = scale === undefined ? (size ?? DEFAULT_SIZE_BY_LEVEL[As]) : undefined;

  return (
    <As
      ref={ref}
      className={cx(
        'nrx-heading',
        scale ? `nrx-heading--scale-${scale}` : `nrx-heading--size-${resolvedSize}`,
        `nrx-heading--${weight}`,
        className,
      )}
      {...rest}
    />
  );
});
